import React, { useRef, useState, useEffect } from 'react';
import {
  CreditCard,
  Store,
  Building2,
  Smartphone,
  Zap,
  Package,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Tablet,
  Monitor,
  Globe
} from 'lucide-react';
import { PlatformInfo } from '../pages/ProductMatrixNew';
import './PlatformCards.css';


interface PlatformCardsProps {
  platforms: PlatformInfo[];
  onPlatformClick: (platform: PlatformInfo) => void;
}

// Map platform names to appropriate icons
const getPlatformIcon = (platformName: string) => {
  const name = platformName.toLowerCase();

  if (name.includes('skytab pos') || name.includes('venue')) {
    return Store;
  }
  if (name.includes('solo')) {
    return Smartphone;
  }
  if (name.includes('acquiring') || name.includes('shift4 acquiring')) {
    return CreditCard;
  }
  if (name.includes('lbm') || name.includes('hospitality')) {
    return Building2;
  }
  if (name.includes('marketplace') || name.includes('integrations')) {
    return Package;
  }

  // Default fallback
  return Zap;
};

// Get hardware icons based on platform name (hard-coded)
const getHardwareIcons = (platform: PlatformInfo): { icon: React.ComponentType<any>, rotate?: boolean }[] => {
  const platformName = platform.name.toLowerCase();
  const icons: { icon: React.ComponentType<any>, rotate?: boolean }[] = [];

  if (platformName.includes('skytab') && !platformName.includes('venue')) {
    // SkyTab POS - all three icons
    icons.push({ icon: Smartphone }); // Mobile
    icons.push({ icon: Tablet, rotate: true }); // Tablet (landscape)
    icons.push({ icon: Monitor }); // Computer
  } else if (platformName.includes('skytab') && platformName.includes('venue')) {
    // SkyTab Venue - mobile and tablet
    icons.push({ icon: Smartphone }); // Mobile
    icons.push({ icon: Tablet, rotate: true }); // Tablet (landscape)
  } else if (platformName.includes('shift4 solo') || platformName.includes('solo')) {
    // Shift4 Solo - mobile only
    icons.push({ icon: Smartphone }); // Mobile
  } else if (platformName.includes('shift4 mobile') || platformName.includes('mobile')) {
    // Shift4 Mobile - mobile only
    icons.push({ icon: Smartphone }); // Mobile
  } else if (platformName.includes('online') || platformName.includes('payments')) {
    // Online Payments - globe and computer
    icons.push({ icon: Globe }); // Globe
    icons.push({ icon: Monitor }); // Computer
  } else if (platformName.includes('lbm')) {
    // LBM - globe and computer
    icons.push({ icon: Globe }); // Globe
    icons.push({ icon: Monitor }); // Computer
  } else if (platformName.includes('3pi') || platformName.includes('integrated payments')) {
    // Integrated Payments - mobile, tablet, and computer
    icons.push({ icon: Smartphone }); // Mobile
    icons.push({ icon: Tablet, rotate: true }); // Tablet (landscape)
    icons.push({ icon: Monitor }); // Computer
  }

  return icons;
};


const PlatformCards: React.FC<PlatformCardsProps> = ({ platforms, onPlatformClick }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeTab, setActiveTab] = useState('All');

  // Tab configuration
  const tabs = [
    { id: 'All', label: 'All' },
    { id: 'Products', label: 'Products' },
    { id: 'Integrated Payments', label: 'Integrated Payments' },
    { id: 'Shift4 Payments', label: 'Payments' },
    { id: 'Onboarding', label: 'Onboarding' }
  ];

  // Function to categorize platforms into tabs
  const categorizePlatform = (platformName: string): string => {
    const name = platformName.toLowerCase();

    if (name.includes('3pi') || name.includes('integrated payments')) {
      return 'Integrated Payments';
    }
    if (name.includes('payments')) {
      return 'Shift4 Payments';
    }
    if (name.includes('skytab') || name.includes('lbm') || name.includes('solo') || name.includes('shift4 mobile')) {
      return 'Products';
    }

    // Default to Products for any other platforms
    return 'Products';
  };

  // Function to assign platform card images - explicit assignment to ensure all images are used
  const getPlatformImage = (platformName: string) => {
    const name = platformName.toLowerCase();

    // Explicit assignments to ensure variety
    if (name.includes('skytab') && !name.includes('venue')) return 'platform-card-x.png'; // SkyTab
    if (name.includes('skytab') && name.includes('venue')) return 'platform-card-x.png'; // SkyTab Venue
    if (name.includes('3pi') || name.includes('integrated payments')) return 'platform-card-x.png'; // Integrated Payments
    if (name.includes('shift4 solo') || name.includes('solo')) return 'platform-card-x.png'; // Shift4 Solo
    if (name.includes('online') || name.includes('payments')) return 'platform-card-x.png'; // Online Payments
    if (name.includes('shift4 mobile') || name.includes('mobile')) return 'platform-card-x.png'; // Shift4 Mobile

    // Fallback for any other platforms
    const images = ['platform-card-x.png', 'platform-card-x.png', 'platform-card-x.png'];
    let hash = 0;
    for (let i = 0; i < platformName.length; i++) {
      hash = ((hash << 5) - hash) + platformName.charCodeAt(i);
      hash = hash & hash;
    }
    const index = Math.abs(hash) % images.length;
    return images[index];
  };

  // Function to get platform order
  const getPlatformOrder = (platformName: string): number => {
    const name = platformName.toLowerCase();
    if (name.includes('skytab') && !name.includes('venue')) return 0; // SkyTab
    if (name.includes('skytab') && name.includes('venue')) return 1; // SkyTab Venue
    if (name.includes('3pi') || name.includes('integrated payments')) return 2; // Integrated Payments
    if (name.includes('shift4 solo') || name.includes('solo')) return 3; // Shift4 Solo
    if (name.includes('online') || name.includes('payments')) return 4; // Online Payments
    if (name.includes('shift4 mobile') || name.includes('mobile')) return 5; // Shift4 Mobile
    return 99; // Everything else at the end
  };

  // Filter platforms based on active tab
  const filteredPlatforms = activeTab === 'All'
    ? platforms
    : platforms.filter(platform => categorizePlatform(platform.name) === activeTab);

  // Sort platforms according to specified order
  const sortedPlatforms = [...filteredPlatforms].sort((a, b) => {
    const orderA = getPlatformOrder(a.name);
    const orderB = getPlatformOrder(b.name);
    return orderA - orderB;
  });

  // Get platform descriptions based on name
  const getPlatformDescription = (platformName: string) => {
    const name = platformName.toLowerCase();

    if (name.includes('skytab') && !name.includes('venue')) {
      return 'For Restaurants';
    }
    if (name.includes('venue')) {
      return 'For Stadiums';
    }
    if (name.includes('solo')) {
      return ' ';
    }
    if (name.includes('lbm')) {
      return 'For Commerce';
    }
    if (name.includes('mobile')) {
      return ' ';
    }
    if (name.includes('3pi') || name.includes('partner integrations')) {
      return 'For Integration';
    }
    return ' ';
  };

  // Check scroll position and update button states
  const updateScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding
    }
  };


  // Handle scroll button clicks
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -296, behavior: 'smooth' }); // Card width (288px) + gap (24px)
      setTimeout(updateScrollButtons, 300); // Update after scroll animation
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 296, behavior: 'smooth' }); // Card width (288px) + gap (24px)
      setTimeout(updateScrollButtons, 300); // Update after scroll animation
    }
  };


  // Wheel event listener for horizontal scrolling (handles both mouse and trackpad)
  useEffect(() => {
    let momentum = 0;
    let animationId: number | null = null;
    let lastTime = 0;

    const applyMomentum = () => {
      if (!scrollRef.current || Math.abs(momentum) < 0.1) {
        momentum = 0;
        animationId = null;
        return;
      }

      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const newScrollLeft = Math.max(0, Math.min(scrollLeft + momentum, maxScroll));

      scrollRef.current.scrollLeft = newScrollLeft;
      updateScrollButtons();

      // Apply friction - increased friction for better control
      momentum *= 0.32;

      // Continue animation
      animationId = requestAnimationFrame(applyMomentum);
    };

    const handleWheel = (e: WheelEvent) => {
      if (scrollRef.current && platforms.length > 0) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        const canScrollHorizontally = scrollWidth > clientWidth;

        if (canScrollHorizontally) {
          // Detect trackpad horizontal swipe - be more aggressive in detection
          const absX = Math.abs(e.deltaX);
          const absY = Math.abs(e.deltaY);
          const isHorizontalSwipe = (absX > absY && absX > 1) || (e.deltaX !== 0 && absX > 3);

          // Handle trackpad horizontal swipes with momentum
          if (isHorizontalSwipe) {
            e.preventDefault();
            e.stopPropagation();

            const currentTime = performance.now();
            const deltaTime = currentTime - lastTime;
            lastTime = currentTime;

            // Add to momentum with velocity consideration - reduced sensitivity
            const velocity = deltaTime > 0 ? e.deltaX / deltaTime : 0;
            momentum += e.deltaX * 0.4 + velocity * 0.1;

            // Cap momentum to prevent excessive speed - reduced max speed
            momentum = Math.max(-20, Math.min(20, momentum));

            // Start momentum animation if not already running
            if (!animationId) {
              animationId = requestAnimationFrame(applyMomentum);
            }

            return;
          }

          // Handle mouse wheel (deltaY only, no deltaX) - convert to horizontal
          const isMouseWheel = e.deltaX === 0 && e.deltaY !== 0;
          if (isMouseWheel) {
            // Check if we can scroll in the intended direction
            const canScrollLeft = scrollLeft > 0;
            const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
            const scrollingLeft = e.deltaY < 0;
            const scrollingRight = e.deltaY > 0;

            if ((scrollingLeft && canScrollLeft) || (scrollingRight && canScrollRight)) {
              e.preventDefault();
              // Clear any trackpad momentum when using mouse
              momentum = 0;
              if (animationId) {
                cancelAnimationFrame(animationId);
                animationId = null;
              }

              // Smooth scroll for mouse wheel using scrollTo with behavior
              const scrollAmount = e.deltaY * 2;
              const targetScrollLeft = Math.max(0, Math.min(
                scrollLeft + scrollAmount,
                scrollWidth - clientWidth
              ));

              scrollRef.current.scrollTo({
                left: targetScrollLeft,
                behavior: 'smooth'
              });

              // Update buttons after a delay to account for smooth scroll
              setTimeout(updateScrollButtons, 100);
            }
          }

          // Let vertical trackpad gestures pass through for page scrolling
          // (when deltaY > deltaX, it's a vertical gesture, don't prevent default)
        }
      }
    };

    // Attach wheel event listener directly to the carousel container
    const carousel = scrollRef.current;
    if (carousel) {
      carousel.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (carousel) {
        carousel.removeEventListener('wheel', handleWheel);
      }
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [platforms]);

  // Update scroll buttons on mount and resize
  useEffect(() => {
    updateScrollButtons();

    const handleResize = () => updateScrollButtons();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [platforms]);

  // Reset scroll position when tab changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
      updateScrollButtons();
    }
  }, [activeTab]);

  return (
    <div className="w-full">
      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 px-4 sm:px-0">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200
                ${isActive
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Horizontal scrolling container */}
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto overflow-y-visible pb-4 pt-8 scrollbar-hide platform-carousel pr-96"
        style={{
          touchAction: 'pan-x',
          overscrollBehaviorX: 'contain',
          overscrollBehaviorY: 'auto'
        }}
      >
        {activeTab === 'Onboarding' ? (
          <div className="flex items-start w-full min-h-48 pl-6">
            <div className="text-left py-12">
              <div className="text-gray-400 mb-4">
                <Package className="w-16 h-16 mb-4" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Coming Soon</h3>
              <p className="text-gray-600">Onboarding tools and resources will be available here.</p>
            </div>
          </div>
        ) : (
          sortedPlatforms.map((platform) => {
          const isAvailable = platform.isAvailable;
          const description = getPlatformDescription(platform.name);
          const platformImage = getPlatformImage(platform.name);
          const hardwareIcons = getHardwareIcons(platform);

          return (
            <div
              key={platform.name}
              className={`
                relative flex-shrink-0 w-72 bg-white rounded-lg p-4 platform-card
                transition-all duration-300 ease-out cursor-pointer shadow-sm border border-gray-200
                ${isAvailable
                  ? ''
                  : 'opacity-60 cursor-not-allowed'
                }
              `}
              style={{
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              }}
              onClick={() => onPlatformClick(platform)}
            >
              {/* Polaroid Image Area */}
              <div className="w-full h-48 bg-gray-100 rounded-md overflow-hidden mb-4 flex items-center justify-center">
                <img
                  src={`/${platformImage}`}
                  alt={platform.name}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Polaroid Text Area - like written on bottom */}
              <div className="text-left space-y-2 px-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 leading-tight">
                    {platform.name}
                  </h3>
                  {/* Hardware Type Icons */}
                  <div className="flex items-center gap-1">
                    {hardwareIcons.map((iconData, index) => {
                      const IconComponent = iconData.icon;
                      return (
                        <IconComponent
                          key={index}
                          className={`w-4 h-4 text-gray-400 ${iconData.rotate ? 'rotate-90' : ''}`}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* Small arrow indicator */}
                <div className="flex justify-end pt-1">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <ArrowRight className="w-3 h-3 text-white" />
                  </div>
                </div>
              </div>

              {/* Disabled Overlay */}
              {!isAvailable && (
                <div className="absolute inset-0 bg-white/90 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-500 mb-1">
                      Currently Not Available
                    </div>
                    <div className="text-xs text-gray-400">
                      Not available in selected region
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        }))}
      </div>

      {/* Navigation buttons positioned below the carousel */}
      <div className="flex justify-start items-center mt-4">
        <div className="flex gap-2">
          <button
            onClick={scrollLeft}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-gray-200 hover:bg-gray-50 transition-all nav-button shadow-sm"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={scrollRight}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-gray-200 hover:bg-gray-50 transition-all nav-button shadow-sm"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

    </div>
  );
};

export default PlatformCards;